import { useState } from 'react';
import { motion, useTransform } from 'framer-motion';
import { useBreakpoint } from '../hooks/useBreakpoint';

const layerData = {
  UNTRACKABLE: {
    number: '01',
    subtitle: 'The Identity Layer',
    module: 'NOISE ENGINE',
    logic: 'Feed them static. Traditional privacy tools just hide you; we confuse them. The Noise Engine floods your digital footprint with high-volume mathematical entropy. It injects false signals and random patterns alongside your real ones.',
    outcome: 'To the algorithm, you are everywhere and nowhere. Your true signal is lost in the noise.',
  },
  INVISIBLE: {
    number: '02',
    subtitle: 'The Execution Layer',
    module: 'THE AIRLOCK',
    logic: 'The internet, behind glass. We enforce a Zero-Trust architecture. Suspicious links and unknown code are executed on our disposable cloud servers, not your device. We stream only the visual feed to you.',
    outcome: 'You were never really there. If a site attacks, it hits our wall, not your hardware.',
  },
  UNBREAKABLE: {
    number: '03',
    subtitle: 'The Asset Layer',
    module: 'THE INTERCEPTOR',
    logic: 'The iron handshake. We secure the final mile: your assets. The Interceptor simulates every transaction in a sandbox before you sign. It predicts the outcome of the contract in real-time. If the code contains malicious logic, the handshake is frozen.',
    outcome: 'The trap snaps shut on empty air. Your sovereignty remains intact.',
  },
};

const ProductLayer = ({ title, index, scrollProgress }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { isMobile, isTablet } = useBreakpoint();
  const data = layerData[title];

  // Responsive spacing and widths
  const spacing = isMobile ? 0 : isTablet ? 260 : 320;
  const x = useTransform(scrollProgress, [0, 1], [0, index * spacing]);

  const getCardWidth = () => {
    if (isMobile) return isExpanded ? 'calc(100vw - 48px)' : 'calc(100vw - 48px)';
    if (isTablet) return isExpanded ? 300 : 240;
    return isExpanded ? 360 : 280;
  };

  return (
    <motion.div
      data-hover
      onClick={() => setIsExpanded(!isExpanded)}
      style={{
        position: isMobile ? 'relative' : 'absolute',
        top: 0,
        left: 0,
        width: getCardWidth(),
        height: isExpanded ? 'auto' : isMobile ? 'auto' : 420,
        minHeight: isMobile ? 300 : 420,
        maxHeight: isMobile && isExpanded ? '80vh' : 'none',
        overflow: isMobile && isExpanded ? 'auto' : 'visible',
        border: `1px solid ${isExpanded ? '#00FF41' : 'rgba(242,242,242,0.3)'}`,
        background: isExpanded ? 'rgba(0,255,65,0.03)' : '#050505',
        padding: isMobile ? 24 : 32,
        zIndex: isExpanded ? 10 : 3 - index,
        x: isMobile ? 0 : x,
        marginBottom: isMobile ? 16 : 0,
        cursor: 'pointer',
      }}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.2 }}
      whileHover={{ scale: 1.02 }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
          height: '100%',
        }}
      >
        {/* Header */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div
            className="font-mono"
            style={{
              fontSize: isMobile ? 10 : 11,
              color: '#00FF41',
              textTransform: 'uppercase',
              letterSpacing: '0.2em',
              marginBottom: 12,
            }}
          >
            {data.number}
          </div>
          <h4
            style={{
              fontSize: isMobile ? 24 : 28,
              fontWeight: 600,
              letterSpacing: '-0.03em',
              color: '#F2F2F2',
              textTransform: 'uppercase',
              marginBottom: 8,
              lineHeight: 1.1,
            }}
          >
            {title}.
          </h4>
          <p
            className="font-mono"
            style={{
              fontSize: isMobile ? 10 : 11,
              color: 'rgba(242,242,242,0.5)',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}
          >
            {data.subtitle}
          </p>
        </div>

        {/* Expandable Content */}
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
            }}
          >
            {/* Logic */}
            <div>
              <div
                className="font-mono"
                style={{
                  fontSize: 9,
                  color: 'rgba(242,242,242,0.4)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  marginBottom: 8,
                }}
              >
                THE LOGIC
              </div>
              <p
                className="font-mono"
                style={{
                  fontSize: isMobile ? 10 : 11,
                  color: 'rgba(242,242,242,0.8)',
                  lineHeight: 1.6,
                  letterSpacing: '0.02em',
                }}
              >
                {data.logic}
              </p>
            </div>

            {/* Outcome */}
            <div>
              <div
                className="font-mono"
                style={{
                  fontSize: 9,
                  color: 'rgba(242,242,242,0.4)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  marginBottom: 8,
                }}
              >
                THE OUTCOME
              </div>
              <p
                className="font-mono"
                style={{
                  fontSize: isMobile ? 10 : 11,
                  color: '#00FF41',
                  lineHeight: 1.6,
                  letterSpacing: '0.02em',
                  fontWeight: 500,
                }}
              >
                {data.outcome}
              </p>
            </div>
          </motion.div>
        )}

      </div>
    </motion.div>
  );
};

export default ProductLayer;
